import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { SaveRounded } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { EmptyState, PageIntro, PageShell, SurfaceCard } from "../ui/AppPrimitives";
import useSnackbar from "../hooks/useSnackbar";

const EditDate = ({ dates, onUpdate }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();

  const dateItem = useMemo(() => dates.find((d) => d.id === id), [dates, id]);
  const fullDateTime = dateItem?.dateTime || "";
  const initialDate = fullDateTime.split("T")[0] || "";
  const initialTime = fullDateTime.includes("T") ? fullDateTime.split("T")[1]?.slice(0, 5) : "";

  const [title, setTitle] = useState(dateItem?.title || "");
  const [category, setCategory] = useState(dateItem?.category || "Other");
  const [notes, setNotes] = useState(dateItem?.notes || "");
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!dateItem) return;
    const safeDateTime = dateItem.dateTime || "";
    setTitle(dateItem.title || "");
    setCategory(dateItem.category || "Other");
    setNotes(dateItem.notes || "");
    setDate(safeDateTime.split("T")[0] || "");
    setTime(safeDateTime.includes("T") ? safeDateTime.split("T")[1]?.slice(0, 5) : "");
  }, [dateItem]);

  if (!dateItem) {
    return (
      <PageShell maxWidth="sm">
        <EmptyState
          title="Date not found"
          description="This date may have been removed or has not loaded yet."
          actionLabel="Return home"
          actionTo="/"
        />
      </PageShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !date) {
      setError("Title and date are required.");
      return;
    }

    setSaving(true);

    try {
      await onUpdate({
        ...dateItem,
        title: title.trim(),
        category,
        notes: notes.trim(),
        dateTime: time ? `${date}T${time}` : `${date}T00:00`,
      });

      showSnackbar("Date updated successfully!", "success");
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    } catch (updateError) {
      console.error(updateError);
      setError("We couldn’t save your changes. Please try again.");
      showSnackbar("Unable to update date.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell maxWidth="md">
      
      <SurfaceCard sx={{ p: { xs: 2.5, md: 4 } }}>
        <form onSubmit={submit}>
          <Stack spacing={2.4}>
            <TextField
              label="Event title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setError("");
              }}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Event date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
              />
            </Stack>

            <TextField select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {["Medical", "Exam", "Legal", "Personal", "Other"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={4}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.2} justifyContent="flex-end">
              <Button
                type="button"
                variant="text"
                color="inherit"
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SaveRounded />}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Stack>
          </Stack>
        </form>
      </SurfaceCard>
    </PageShell>
  );
};

export default EditDate;
